import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQ_ITEMS = [
  {
    question: 'Muss ich nach jedem Unfall ein Gutachten machen lassen?',
    answer: 'Nein. Bei kleineren Schäden reicht oft ein Kostenvoranschlag. Bei größeren Schäden oder wenn Wertminderung und Nutzungsausfall relevant sind, ist ein Gutachten sinnvoll. Die Entscheidung ist einzelfallabhängig – wir beraten Sie gerne, bieten aber keine Rechtsberatung an.'
  },
  {
    question: 'Wer bezahlt ein Unfallgutachten?',
    answer: 'Bei einem unverschuldeten Unfall trägt in der Regel die gegnerische Haftpflichtversicherung die Gutachterkosten – je nach Einzelfall und Schadenssituation. Diese Information stellt keine rechtsverbindliche Zusage dar.'
  },
  {
    question: 'Was ist ein Bagatellschaden?',
    answer: 'Ein Bagatellschaden ist ein Schaden geringer Höhe, bei dem häufig ein Kostenvoranschlag ausreicht. Die genaue Grenze ist einzelfallabhängig und sollte im konkreten Fall geprüft werden.'
  },
  {
    question: 'Wie schnell sollte ich nach einem Unfall handeln?',
    answer: 'Möglichst zeitnah. Die Schadensmeldung sollte innerhalb weniger Tage erfolgen. Dokumentation und Fotos am besten direkt am Unfallort erstellen.'
  },
  {
    question: 'Muss ich mein Fahrzeug bewegen lassen?',
    answer: 'Nur wenn es die Sicherheit erfordert. Ansonsten ist es oft ratsam, das Fahrzeug erst nach der Dokumentation (Fotos, ggf. Polizei) zu bewegen.'
  },
  {
    question: 'Was, wenn ich unsicher bin, wie ich vorgehen soll?',
    answer: 'Bei Unsicherheiten können Sie sich unverbindlich beraten lassen. Wir helfen Ihnen gerne weiter und klären, welche Schritte in Ihrer konkreten Situation sinnvoll sind.'
  },
  {
    question: 'Kommt ein Gutachter auch zu mir?',
    answer: 'Ja. Die Begutachtung findet direkt vor Ort statt – bundesweit. Sie müssen nicht zu uns fahren.'
  },
  {
    question: 'Was passiert nach der Begutachtung?',
    answer: 'Sie erhalten das fertige Gutachten zur Vorlage bei der Versicherung. Es dient als Grundlage für die Schadenregulierung. Bei Fragen unterstützen wir Sie gerne.'
  },
  {
    question: 'Kann ich selbst einen Gutachter wählen?',
    answer: 'Ja. Als Geschädigter haben Sie das Recht auf freie Gutachterwahl. Sie sind nicht verpflichtet, einen von der Versicherung vorgeschlagenen Gutachter zu beauftragen.'
  },
  {
    question: 'Wie lange dauert ein Gutachten?',
    answer: 'Die Vor-Ort-Begutachtung dauert meist 30–60 Minuten. Das schriftliche Gutachten erhalten Sie in der Regel innerhalb von 2–3 Werktagen.'
  },
  {
    question: 'Was mache ich, wenn die Versicherung das Gutachten ablehnt?',
    answer: 'In solchen Fällen empfiehlt es sich, rechtlichen Rat einzuholen. Wir selbst bieten keine Rechtsberatung an. Jeder Fall ist individuell – wir können Sie jedoch zum weiteren Vorgehen orientieren.'
  },
  {
    question: 'Kann ich trotz Gutachten selbst entscheiden, wo ich reparieren lasse?',
    answer: 'Ja. Die Wahl der Werkstatt liegt grundsätzlich bei Ihnen. Das Gutachten dient lediglich der Schadensbewertung und gibt Ihnen eine objektive Grundlage. Bei Fragen zu Ihren Ansprüchen empfiehlt sich rechtliche Beratung.'
  },
  {
    question: 'Was ist, wenn der Unfallgegner keine Versicherung hat?',
    answer: 'Das ist eine komplexe Situation, die einzelfallabhängig zu bewerten ist. In solchen Fällen sollten Sie zeitnah rechtlichen Rat einholen, um Ihre Ansprüche zu klären und zu sichern. Wir bieten keine Rechtsberatung an.'
  },
  {
    question: 'Muss ich vor der Reparatur warten?',
    answer: 'In vielen Fällen ist es ratsam, vor Reparaturbeginn eine Begutachtung durchführen zu lassen. So können alle Ansprüche korrekt dokumentiert werden.'
  }
];

export const FAQNachDemUnfall = () => {
  return (
    <section className="py-28 lg:py-36 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/8 text-brand-green-dark rounded-full text-sm font-semibold mb-6 border border-brand-green/15">
            FAQ
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Häufig gestellte Fragen
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-normal">
            Antworten auf die wichtigsten Fragen nach einem Unfall
          </p>
        </div>

        <div className="bg-white border border-gray-100 rounded-3xl p-10 lg:p-16 shadow-modern-lg">
          <Accordion type="single" collapsible className="w-full space-y-5">
            {FAQ_ITEMS.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`} 
                className="border border-gray-200 rounded-2xl px-7 py-1 data-[state=open]:border-brand-green/40 data-[state=open]:shadow-xl transition-all bg-white"
              >
                <AccordionTrigger className="text-left font-bold text-gray-900 hover:text-brand-green py-6 hover:no-underline text-base lg:text-lg">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-6 h-6 text-brand-green flex-shrink-0 mt-0.5" />
                    <span className="pr-4">{item.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pb-6 pl-10 text-[15px]">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-10 text-center">
          <a href="/unfallgutachten" className="text-brand-green hover:text-brand-green-dark font-semibold text-lg transition-colors">
            Mehr zum Unfallgutachten →
          </a>
        </div>
      </div>
    </section>
  );
};